/**
 * Customer profile — a read-only panel that aggregates everything we know about one
 * customer: every rental (contract) they ever had, the cars they took, and their
 * traffic fines, and the Salik / Darb / parking of each contract. The customer is Ejar's customer number (owner, 4 Oct
 * 2026: one customer, a passport on one contract and a Saudi ID on the next), else his IDENTITY; a contract with no ID
 * on file takes the other no-ID contracts of its phone, else stands alone - the ONE rule Ask Yelo's list uses too
 * (contactsService.customerKeyOf, owner 28 Sep 2026), so the count it shows is what opens here. Rental history comes
 * from the in-memory contacts store (no reads); a
 * contract not in the store yet is read by its exact number from the contract records
 * (airport_contracts / contracts) with its real status. Nothing is ever invented: a
 * number found nowhere says "No contract ... found - check the number".
 * Fines are matched by the fine's own contract_no (the engine already attributes
 * each fine to a contract), so amounts and dates are exact.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import contactsService, { formatPhone, whatsappUrl, phoneDigits, contractKey } from '../services/contactsService';
import { findContractRecord, isOpenStatus, contractNotFoundText, contractUncheckedText } from '../services/contractRecord';
import { resolveProfileRows, openNowCount } from './customerProfileRows';
import { formatPlateNumber } from '../utils/plateFormatter';
import logger from '../utils/logger';
import Dialog from '../ui/Dialog/Dialog';
import Tabs from '../ui/Tabs/Tabs';
import DataTable from '../ui/DataTable/DataTable';
import { CellText, CellId, CellPlate, CellMoney, CellStatus, CellMuted } from '../ui/DataTable/cells';
import Stat, { StatGrid } from '../ui/Stat/Stat';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import { ICONS } from '../ui/icon-map';
import EmptyState from '../ui/EmptyState';
import LoadingSpinner from './LoadingSpinner';
import CustomerCharges from './CustomerCharges';
import { profileFines, totalOwed } from './customerProfileFines';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../constants/permissions';
import { formatMoney } from '../constants/finance';
import { formatContractDateTime } from '../utils/contractDateUtils';
import { USE_CONTRACTS_V2, v2RecordsByIds } from '../services/contractsV2';

// dd/mm/yyyy[ HH:MM] -> Date (or null)
const parseDMY = (s) => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/.exec(String(s || ''));
    if (!m) return null;
    return new Date(+m[3], +m[2] - 1, +m[1], +(m[4] || 0), +(m[5] || 0));
};
// A contract's pick-up / drop-off as Yelo shows it everywhere: "dd/mm/yyyy HH:MM" when the
// value has a time, "dd/mm/yyyy" when it has none (owner, 27 Sep 2026). Display only - the
// sorting above still uses parseDMY. Text the helper cannot read is shown as it is.
const fmtWhen = (s) => {
    if (!s) return '—';
    return formatContractDateTime(s) || String(s);
};
// First / Last tile: date and time stay together on one line when the tile is wide enough
// and wrap to two centred lines (date, then time) when it is not - never cut off.
const StatWhen = ({ value }) => {
    const text = fmtWhen(value);
    const m = /^(\S+) (\d{2}:\d{2})$/.exec(text);
    return (
        <span style={{ display: 'inline-block', fontSize: 'var(--text-lg)', lineHeight: 'var(--leading-tight)', whiteSpace: 'normal', textAlign: 'center' }}>
            {m ? (<><span style={{ whiteSpace: 'nowrap' }}>{m[1]}</span> <span style={{ whiteSpace: 'nowrap' }}>{m[2]}</span></>) : text}
        </span>
    );
};
// Open = Ejar's "Open" only: an empty / unknown status is shown as — and never counted as open.
const isOpen = isOpenStatus;
// the one money format (DUP-27): 2 decimals, en-AE grouping, AED first, empty -> —
const money = (n) => formatMoney(n);

// module-level cache: contract no -> { plate, model } (re-opening a customer is free)
const carCache = new Map();

async function fetchCar(contractNo) {
    if (carCache.has(contractNo)) return carCache.get(contractNo);
    if (USE_CONTRACTS_V2) {
        // the contracts store: the contract's car now (its E-Checks above give every car it had)
        const rec = (await v2RecordsByIds([contractNo], 'all').catch(() => []))[0];
        const car = { plate: rec?.car?.plate || '', model: rec?.car?.model || '' };
        carCache.set(contractNo, car);
        return car;
    }
    const readDoc = async (col) => {
        try { const snap = await getDoc(doc(db, col, contractNo)); return snap.exists() ? snap.data() : null; }
        catch (e) { return null; }
    };
    let d = await readDoc('airport_contracts');
    let plate = d && (d['Plate No.'] || d.plate_no || d.plate || '');
    let model = d && (d['Model'] || d.model || '');
    if (!plate) {
        d = await readDoc('contracts');
        plate = d && (d.plate_no || d['Plate No.'] || d.plate || '');
        model = d && (d.model || d['Model'] || '');
    }
    const car = { plate: plate || '', model: model || '' };
    carCache.set(contractNo, car);
    return car;
}

export default function CustomerProfileModal({ open, onClose, seed, onCopy }) {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [matchedBy, setMatchedBy] = useState('none');
    const [missing, setMissing] = useState(null);          // null | 'none' (found nowhere) | 'error' (could not check)
    const [cars, setCars] = useState({});
    const [tab, setTab] = useState('rentals');
    const [fines, setFines] = useState(null);
    const [finesLoading, setFinesLoading] = useState(false);
    const [finesError, setFinesError] = useState(null);   // a failed read is not "no fines" (CON-13)
    const { hasPermission } = usePermissions();
    const canEngine = hasPermission(PERMISSIONS.view_engine);

    const copy = useCallback((text, msg) => {
        const value = String(text ?? '');
        if (!value) return;
        const legacy = () => {
            try {
                const ta = document.createElement('textarea');
                ta.value = value; ta.style.position = 'fixed'; ta.style.top = '-9999px';
                document.body.appendChild(ta); ta.select();
                const ok = document.execCommand('copy'); document.body.removeChild(ta); return ok;
            } catch (e) { return false; }
        };
        try {
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(value).then(() => onCopy && onCopy(msg)).catch(() => { if (legacy()) onCopy && onCopy(msg); });
            } else if (legacy()) onCopy && onCopy(msg);
        } catch (e) { if (legacy()) onCopy && onCopy(msg); }
    }, [onCopy]);

    // Match fines to this customer by the fine's attributed contract (the `Contract`
    // field the engine writes onto each traffic_fines doc). Field names come from
    // the uploaded traffic file: `Contract`, `Ticket Fee`, `Ticket Date`, `Plate_Number`.
    const lastFineRowsRef = useRef(null);
    const loadFines = useCallback(async (contractRows) => {
        lastFineRowsRef.current = contractRows;
        setFinesError(null);
        setFinesLoading(true);
        try {
            if (!(contractRows || []).some(r => contractKey(r.c))) { setFines([]); return; }
            // Source of truth for the fine itself = the fines engine (same as the Fines
            // page), read from the one-time in-memory index (instant, no per-contract call).
            // Every contract of the customer (the index is in memory - no 30-contract cap, CON-23),
            // with the engine's own charge / owed per fine (DUP-17) - never the contract's status.
            const { finesIndex } = await import('../services/finesIndex');
            await finesIndex.ensureLoaded();
            setFines(profileFines(contractRows, (cn) => finesIndex.forContract(cn), formatPlateNumber));
        } catch (e) {
            logger.warn('[customerProfile] fines load failed:', e);
            setFinesError(e?.message || 'the fines service did not answer');
            setFines([]);
        } finally {
            setFinesLoading(false);
        }
    }, []);

    // Resolve the customer's contracts, then enrich cars + fines.
    useEffect(() => {
        if (!open || !seed) return;
        let alive = true;
        setLoading(true); setTab('rentals'); setFines(null); setCars({}); setMissing(null);
        (async () => {
            // the contacts store in memory - with the contracts store (USE_CONTRACTS_V2) the clicked contract and every
            // contract of its customer's identity, read from the store first
            await contactsService.prepareCustomer({ identity: seed.identity, contractNo: seed.contractNo, phone: seed.phone });
            if (!alive) return;
            const resolved = await resolveProfileRows(seed, {
                store: contactsService,
                findRecord: (no) => findContractRecord(no),
                onError: (err) => logger.warn('[customerProfile] contract lookup failed:', err),
            });
            if (!alive) return;
            if (resolved.missing) {
                setRows([]); setMatchedBy('none'); setMissing(resolved.missing); setLoading(false);
                return;
            }
            const { by, rows: found } = resolved;
            // Seed the current contract's car from the clicked row (instant, no read).
            if (seed.contractNo && seed.plate) carCache.set(contractKey(seed.contractNo), { plate: seed.plate, model: seed.model || '' });
            setRows(found); setMatchedBy(by); setLoading(false);
            loadFines(found);

            // Cars per contract: the E-Check archive records every plate checked in/out
            // under a contract, so a contract where the customer switched cars shows ALL
            // of them. (A contract doc only keeps the current plate.)
            const carsOut = {};
            try {
                const { archiveService } = await import('../services/archiveService');
                const recs = await archiveService.findByContractNumbers(found.map(r => r.c));
                if (!alive) return;
                for (const rec of recs || []) {
                    const cn = contractKey(rec['Contract No.'] || rec['Contract No'] || '');
                    const plate = rec['Plate Number'] || rec['Plate No.'] || '';
                    if (!cn || !plate) continue;
                    const key = String(plate).replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                    const m = (carsOut[cn] = carsOut[cn] || new Map());
                    if (!m.has(key)) m.set(key, { plate, model: rec['Model'] || rec.Model || '' });
                }
            } catch (e) { /* fall back to the contract doc below */ }
            // Always include the clicked contract's current plate.
            if (seed.contractNo && seed.plate) {
                const cn = contractKey(seed.contractNo);
                const key = String(seed.plate).replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                const m = (carsOut[cn] = carsOut[cn] || new Map());
                if (!m.has(key)) m.set(key, { plate: seed.plate, model: seed.model || '' });
            }
            const finalized = {};
            for (const cn in carsOut) finalized[cn] = [...carsOut[cn].values()];
            if (alive) setCars(finalized);
            // Contracts with no E-Check plate: fall back to the contract doc's plate.
            const missing = found.filter(r => !(finalized[r.c] && finalized[r.c].length)).slice(0, 40);
            const pool = 6;
            for (let i = 0; i < missing.length; i += pool) {
                const batch = missing.slice(i, i + pool);
                const got = await Promise.all(batch.map(r => fetchCar(r.c).then(car => [r.c, car])));
                if (!alive) return;
                setCars(prev => { const next = { ...prev }; got.forEach(([c, car]) => { if (car.plate) next[c] = [{ plate: car.plate, model: car.model }]; }); return next; });
            }
        })();
        return () => { alive = false; };
    }, [open, seed, loadFines]);

    const profile = useMemo(() => {
        if (!rows.length) return null;
        const name = rows.map(r => r.n).find(Boolean) || seed?.name || 'Customer';
        const phone = rows.map(r => r.p).find(Boolean) || phoneDigits(seed?.phone || '');
        const identity = rows.map(r => r.i).find(Boolean) || seed?.identity || '';
        const type = rows.map(r => r.t).find(Boolean) || '';
        const openNow = openNowCount(rows);
        // first / last rental from the dates themselves (the list starts with the open contract)
        const dated = rows.filter(r => parseDMY(r.d)).sort((a, b) => parseDMY(a.d) - parseDMY(b.d));
        return { name, phone, identity, type, count: rows.length, openNow, first: dated[0]?.d, last: dated[dated.length - 1]?.d };
    }, [rows, seed]);

    const carSummary = useMemo(() => {
        const map = new Map();
        for (const r of rows) {
            for (const car of (cars[r.c] || [])) {
                if (!car.plate) continue;
                const key = String(car.plate).replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                const prev = map.get(key) || { plate: formatPlateNumber(car.plate), model: car.model, count: 0 };
                prev.count += 1;
                if (!prev.model && car.model) prev.model = car.model;
                map.set(key, prev);
            }
        }
        return [...map.values()].sort((a, b) => b.count - a.count);
    }, [rows, cars]);

    if (!open) return null;
    const finesTotal = fines ? totalOwed(fines) : 0;
    const finesLabel = finesLoading && fines === null ? 'Fines …' : finesError ? 'Fines (not loaded)' : `Fines${fines ? ` (${fines.length})` : ''}`;

    const rentalColumns = [
        {
            key: 'c', header: 'Contract', nowrap: true,
            // a click opens the app-wide quick menu: Open in Ejar / Search in The Engine / Copy
            cell: (r) => <CellId>{r.c}</CellId>,
        },
        {
            key: 'car', header: 'Car', sortable: false, searchable: false,
            cell: (r) => {
                const car = cars[r.c];
                if (!car) return <CellMuted>…</CellMuted>;
                if (car.length === 0) return <span className="ds-cell-empty">—</span>;
                return (
                    <div className="flex flex-col items-center gap-0.5">
                        {car.map((c, ci) => (
                            <span key={ci} className="flex flex-col items-center">
                                <CellPlate>{formatPlateNumber(c.plate)}</CellPlate>
                                <CellMuted>{c.model}</CellMuted>
                            </span>
                        ))}
                    </div>
                );
            },
        },
        { key: 'd', header: 'From', nowrap: true, sortValue: (r) => parseDMY(r.d)?.getTime() || 0, cell: (r) => <span className="ds-cell-date">{fmtWhen(r.d)}</span> },
        { key: 'e', header: 'To', nowrap: true, sortValue: (r) => parseDMY(r.e)?.getTime() || 0, cell: (r) => <span className="ds-cell-date">{fmtWhen(r.e)}</span> },
        { key: 'b', header: 'Branch', cell: (r) => <CellText max={160}>{r.b}</CellText> },
        { key: 's', header: 'Status', cell: (r) => (String(r.s || '').trim() ? <CellStatus status={isOpen(r.s) ? 'Open' : r.s} /> : <span className="ds-cell-empty">—</span>) },
    ];

    const fineColumns = [
        { key: 'ticket', header: 'Ticket', cell: (f) => <CellId>{f.ticket}</CellId> },
        { key: 'plate', header: 'Plate', nowrap: true, cell: (f) => <CellPlate>{f.plate}</CellPlate> },
        { key: 'date', header: 'Date & Time', nowrap: true, cell: (f) => (f.date ? <span className="ds-cell-date">{f.date}</span> : <span className="ds-cell-empty">—</span>) },
        {
            key: 'contractNo', header: 'Contract', nowrap: true,
            cell: (f) => (
                <span className="inline-flex items-center gap-1.5">
                    <CellId>{f.contractNo}</CellId>
                    {f.onOpen && <Badge size="sm" variant="success">Current</Badge>}
                </span>
            ),
        },
        {
            key: 'rtaStatus', header: 'RTA status',
            cell: (f) => (f.rtaStatus ? <CellStatus status={f.rtaStatus} tone={/paid/i.test(f.rtaStatus) ? 'success' : 'danger'} /> : <span className="ds-cell-empty">—</span>),
        },
        {
            // the engine's rule (owed_not_on_ejar): owed until Ejar carries it; a fine the customer paid at RTA is never billed
            key: 'ejar', header: 'Ejar',
            cell: (f) => (f.ejar === 'customer'
                ? <Badge size="sm" variant="neutral">Paid by customer at RTA</Badge>
                : <Badge size="sm" variant={f.ejar === 'on' ? 'success' : 'danger'}>{f.ejar === 'on' ? 'On Ejar' : 'Not on Ejar'}</Badge>),
        },
        { key: 'charge', header: 'Charged', cell: (f) => (f.charge ? <CellMoney value={f.charge} /> : <span className="ds-cell-empty">—</span>) },
        { key: 'owed', header: 'Owed', cell: (f) => (f.owed ? <CellMoney value={f.owed} /> : <span className="ds-cell-empty">—</span>) },
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            size="xl"
            title={loading ? 'Loading…' : missing ? 'Contract not found' : (profile?.name || seed?.name || 'Customer')}
            description="Customer profile"
        >
            <div id="customer-profile-modal" className="flex flex-col gap-4 text-gray-900">
                {profile && (
                    <div className="flex flex-wrap items-center gap-2">
                        {profile.identity && <Badge variant="neutral">ID: {profile.identity}</Badge>}
                        {profile.type && <Badge variant="neutral">{profile.type}</Badge>}
                        {profile.count >= 3 && <Badge variant="warning"><Icon glyph={ICONS.star} size="xs" /> Repeat customer</Badge>}
                        {/* how the contracts were linked (contactsService.customerContracts: identity / phone / current) */}
                        {matchedBy === 'phone' && <Badge variant="info">linked by phone (no ID on file)</Badge>}
                        {matchedBy === 'current' && <Badge variant="info">current rental only (no ID on file)</Badge>}
                    </div>
                )}

                {loading ? (
                    <LoadingSpinner message="Loading customer history…" />
                ) : missing === 'none' ? (
                    <EmptyState icon={ICONS.search} title={contractNotFoundText(seed?.contractNo)} />
                ) : missing === 'error' ? (
                    <EmptyState icon={ICONS.error} title={contractUncheckedText(seed?.contractNo)} />
                ) : !profile ? (
                    <EmptyState icon={ICONS.users} title="No history found for this customer." />
                ) : (
                    <>
                        <StatGrid min={130}>
                            <Stat label="Rentals" value={profile.count} />
                            <Stat label="Open now" value={profile.openNow} tone={profile.openNow ? 'success' : undefined} />
                            <Stat label="Cars" value={carSummary.length || '…'} />
                            <Stat label="Fines" value={fines ? fines.length : '…'} tone={fines && fines.length ? 'danger' : undefined} />
                            {/* dates in a smaller size: a full date + time does not fit the big figure style */}
                            <Stat label="First" value={<StatWhen value={profile.first} />} />
                            <Stat label="Last" value={<StatWhen value={profile.last} />} />
                        </StatGrid>

                        <div className="flex flex-wrap items-center gap-2">
                            {profile.phone ? (
                                <>
                                    <Icon glyph={ICONS.phone} size="sm" tone="muted" />
                                    <Button variant="link" size="sm" onClick={() => copy(formatPhone(profile.phone), 'Phone copied!')} title="Copy phone"><span className="ds-cell-phone">{formatPhone(profile.phone)}</span></Button>
                                    <a href={whatsappUrl(profile.phone)} target="_blank" rel="noopener noreferrer" className="yelo-btn yelo-btn--secondary yelo-btn--sm">
                                        <span className="yelo-btn__label">WhatsApp</span>
                                    </a>
                                </>
                            ) : (
                                <span className="text-xs text-gray-500">no phone on file</span>
                            )}
                        </div>

                        {carSummary.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {carSummary.slice(0, 8).map((c) => (
                                    <span key={c.plate} className="inline-flex items-center gap-1.5 border border-gray-200 bg-gray-50 rounded-md px-2 py-1 text-xs">
                                        <span className="font-mono font-medium text-gray-900">{formatPlateNumber(c.plate)}</span>
                                        {c.model && <span className="text-gray-600">{c.model}</span>}
                                        <Badge size="sm" variant="accent">{c.count}×</Badge>
                                    </span>
                                ))}
                            </div>
                        )}

                        <Tabs
                            label="Customer history"
                            idPrefix="cprofile"
                            value={tab}
                            onChange={setTab}
                            items={[
                                { id: 'rentals', label: `Rentals (${profile.count})` },
                                { id: 'fines', label: finesLabel },
                                ...(canEngine ? [{ id: 'charges', label: 'Salik & parking' }] : []),
                            ]}
                        />

                        <div id={`cprofile-panel-${tab}`} role="tabpanel" aria-labelledby={`cprofile-${tab}`}>
                            {tab === 'charges' ? (
                                <CustomerCharges contracts={rows} canPull={hasPermission(PERMISSIONS.pull_salik) || hasPermission(PERMISSIONS.control_engine)} />
                            ) : tab === 'rentals' ? (
                                <DataTable
                                    columns={rentalColumns}
                                    data={rows}
                                    rowKey={(r, i) => r.c + i}
                                    searchable={false}
                                    pagination={false}
                                    columnToggle={false}
                                    maxHeight="50vh"
                                    emptyTitle="No rentals found"
                                />
                            ) : finesLoading || fines === null ? (
                                <LoadingSpinner message="Loading fines…" />
                            ) : finesError ? (
                                <EmptyState icon={ICONS.error} title="Fines could not be loaded" description={`Error: ${finesError}`}
                                    action={<Button size="sm" variant="secondary" onClick={() => loadFines(lastFineRowsRef.current)}>Retry</Button>} />
                            ) : fines.length === 0 ? (
                                <EmptyState icon={ICONS.fines} title="No fines linked to this customer's contracts." />
                            ) : (
                                <DataTable
                                    title={`${fines.length} fine(s)`}
                                    description={`${fines.filter(f => f.onOpen).length} on current contract · ${fines.filter(f => !f.onOpen).length} on past`}
                                    headerActions={<span className="text-sm font-semibold text-red-700">Not on Ejar yet: {money(finesTotal)}</span>}
                                    columns={fineColumns}
                                    data={fines}
                                    rowKey={(f, i) => (f.ticket || '') + i}
                                    rowClassName={(f) => (f.onOpen ? 'bg-purple-50' : '')}
                                    searchable={false}
                                    pagination={false}
                                    columnToggle={false}
                                    maxHeight="50vh"
                                />
                            )}
                        </div>
                    </>
                )}
            </div>
        </Dialog>
    );
}
